import { useState, useEffect, useRef } from 'react';
import emailjs from '@emailjs/browser';
import './Contact.css';

const YARD_SIGN = '/favicon.png';

const EMAILJS_SERVICE_ID = 'service_lvmu546';
const EMAILJS_TEMPLATE_ID = 'template_x5nes0w';
const EMAILJS_PUBLIC_KEY = 'A7rmL3TqJ9MplY1M7';

function Contact() {
  const pageRef = useRef();
  const formRef = useRef();
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    const els = pageRef.current?.querySelectorAll('.animate');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await emailjs.sendForm(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        formRef.current,
        { publicKey: EMAILJS_PUBLIC_KEY }
      );
      setStatus('sent');
      setFormData({ name: '', email: '', message: '' });
      setTimeout(() => setStatus('idle'), 4000);
    } catch (err) {
      console.error('EmailJS error:', err);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 4000);
    }
  };

  return (
    <div className="contact-page" ref={pageRef}>
      {/* HERO */}
      <section className="contact-hero">
        <div className="contact-hero__inner">
          <span className="label animate">Get in Touch</span>
          <h1 className="animate">Share your concerns</h1>
          <p className="animate">
            Kevin is here to listen. Reach out with your ideas, questions,
            or concerns about the future of Horseheads.
          </p>
        </div>
      </section>

      {/* CONTACT BODY */}
      <section className="contact-body">
        <div className="contact-body__inner">
          {/* INFO SIDE */}
          <div className="contact-info animate">
            <div className="contact-info__card">
              <img src={YARD_SIGN} alt="Adams for Mayor" className="contact-info__badge" />
              <h3>Kevin Adams</h3>
              <p>Mayor of Horseheads</p>
            </div>

            <div className="contact-info__links">
              <a href="mailto:concerns@kevindadams.com" className="contact-link">
                <div className="contact-link__icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="20" height="20">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </div>
                <div>
                  <span className="contact-link__label">Email</span>
                  <span className="contact-link__value">concerns@kevindadams.com</span>
                </div>
              </a>

              <a href="tel:+16073980783" className="contact-link">
                <div className="contact-link__icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="20" height="20">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                </div>
                <div>
                  <span className="contact-link__label">Phone</span>
                  <span className="contact-link__value">(607) 398-0783</span>
                </div>
              </a>

              <a
                href="https://www.facebook.com/KevinAdamsNY/"
                target="_blank"
                rel="noopener noreferrer"
                className="contact-link"
              >
                <div className="contact-link__icon">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                  </svg>
                </div>
                <div>
                  <span className="contact-link__label">Facebook</span>
                  <span className="contact-link__value">KevinAdamsNY</span>
                </div>
              </a>

              <a
                href="https://www.linkedin.com/in/kevinadams313/"
                target="_blank"
                rel="noopener noreferrer"
                className="contact-link"
              >
                <div className="contact-link__icon">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.37V9h3.41v1.56h.05c.47-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45C23.2 24 24 23.23 24 22.28V1.72C24 .77 23.2 0 22.22 0z" />
                  </svg>
                </div>
                <div>
                  <span className="contact-link__label">LinkedIn</span>
                  <span className="contact-link__value">kevinadams313</span>
                </div>
              </a>
            </div>
          </div>

          {/* FORM SIDE */}
          <form className="contact-form animate" ref={formRef} onSubmit={handleSubmit}>
            <h2>Send a message</h2>
            <p>Send Kevin a message directly. He'll get back to you as soon as possible.</p>

            <div className="form-field">
              <label htmlFor="name">Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your full name"
                required
              />
            </div>

            <div className="form-field">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                required
              />
            </div>

            <div className="form-field">
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="What would you like Kevin to know?"
                rows="5"
                required
              />
            </div>

            <button
              type="submit"
              className="btn btn--primary btn--full"
              disabled={status === 'sending'}
            >
              {status === 'sending' && 'Sending...'}
              {status === 'sent' && 'Receipt received'}
              {status === 'error' && 'Something went wrong — try again'}
              {status === 'idle' && 'Send Message'}
              {status === 'idle' && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              )}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

export default Contact;
